/* eslint-disable max-classes-per-file */

import GlobalController from '../base-controllers/GlobalController';
import { HTTP_EVENT, PAGE_UPDATER_EVENT } from '../../constants';
import { parseHTML, selectSingle, triggerCustomEvent } from '../utils/dom';
import { afterNextPaint } from '../utils/js';
import initializer from '../initializer';

interface PageUpdate {
  type: string;
  id: string;
  content?: DocumentFragment;
}

interface ResponseBody {
  pageUpdates?: PageUpdate[];
}

const pageUpdateCommands = {
  append: function append(element, content) {
    element.append(content);
  },

  prepend: function prepend(element, content) {
    element.prepend(content);
  },

  replace: function replace(element, content) {
    element.replaceWith(content);
  },

  update: function update(element, content) {
    element.innerHTML = '';
    element.append(content);
  },

  remove: function remove(element) {
    element.remove();
  },
};

/**
 * Perform a page update.
 */
class PageUpdate {
  constructor(type, id, content) {
    this.type = type;
    this.id = id;
    this.content = parseHTML(content);
  }

  getElement() {
    return selectSingle(`#${this.id}`) as HTMLElement;
  }

  perform() {
    const command = pageUpdateCommands[this.type];
    if (!command) {
      if (process.env.NODE_ENV === 'development') {
        window.console.error(`Invalid page update command '${this.type}'`);
      }
      return;
    }

    const element = this.getElement();
    if (!element) {
      if (process.env.NODE_ENV === 'development') {
        window.console.error(
          `Can't find page update element with id '${this.id}'`,
        );
      }
      return;
    }

    if (['replace', 'remove'].includes(this.type)) {
      initializer.destroy(element);
    } else if (this.type === 'update' && element.children.length) {
      // The node itself is the same after an update, only destroy children.
      Array.from(element.children).forEach((child) => {
        initializer.destroy(child);
      });
    }

    command(element, this.content);

    // The same element will not be in the DOM after a replace.
    const newElement = this.type === 'replace' ? this.getElement() : element;

    // Nothing to init after a remove.
    if (this.type !== 'remove') {
      initializer.init(newElement);
    }

    triggerCustomEvent(
      newElement,
      PAGE_UPDATER_EVENT.update,
      this.type,
      this.id,
    );
  }
}

/**
 * Handle page updates.
 *
 * Update the page with components that have a type, id and possibly content:
 *
 * - type: The type of command.
 * - id: The element id.
 * - [content]: An HTML fragment. Used for all types except remove.
 *
 * Events triggered on the affected element:
 *
 * | Name                      | Arguments | Triggered                      |
 * |---------------------------|-----------|--------------------------------|
 * | PAGE_UPDATER_EVENT.update | type, id  | After the update has been made |
 */
export default class PageUpdater extends GlobalController {
  static id = 'PageUpdater';

  private pageUpdatesQueue: any[];

  init() {
    super.init();
    this.pageUpdatesQueue = [];
    // @ts-ignore
    this.onGlobal(HTTP_EVENT.success, this.handleAjaxSuccess);
  }

  /**
   * Check for page updates in ajax response
   *
   * @param event - The ajax:success event
   * @param response - The ajax response
   * @param body - The response body
   */
  handleAjaxSuccess = (
    event: Event,
    response: Response,
    body: ResponseBody,
  ) => {
    if (body?.pageUpdates) {
      this.pageUpdater(body.pageUpdates);
    }
  };

  /**
   * Add and perform page updates
   *
   * @param updates - The page updates
   */
  pageUpdater = (updates: PageUpdate[]) => {
    updates.forEach(({ content, id, type }) => {
      this.pageUpdatesQueue.push(new PageUpdate(type, id, content));
    });

    afterNextPaint(() => {
      while (this.pageUpdatesQueue.length) {
        this.pageUpdatesQueue.shift().perform();
      }
    });
  };
}
